<template>
  <b-form-group
    :id="`${name}-group`"
    :description="description"
    :label="label"
    :label-for="`${name}-ID`"
    :invalid-feedback="invalidFeedback"
    :valid-feedback="validFeedback"
    :state="state"
    :class="`${required ? 'required' : ''}`"
  >
    <div class="text-editors" @mouseleave="hideEmojiPicker">
      <quill-editor
        class="editor"
        ref="textEditors"
        :value="value"
        :options="editorOption"
        :disabled="disabled"
        :key="showEmojiButton"
        @change="onEditorChange"
        @blur="onEditorBlur($event)"
        @focus="onEditorFocus($event)"
        @ready="onEditorReady($event)"
      />
      <picker
        v-if="showEmoji"
        @select="addEmoji"
        class="emoji-picker"
        :set="'google'"
        :style="{
          position: 'absolute',
          zIndex: 1000,
          top: '40px',
          left: '10px',
        }"
      />
    </div>
  </b-form-group>
</template>

<script>
import hljs from 'highlight.js';
import debounce from 'lodash/debounce';
import { quillEditor } from 'vue-quill-editor';
import { Picker } from 'emoji-mart-vue';
import 'emoji-mart-vue/data/all.json';
import 'highlight.js/styles/tomorrow.css';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';

export default {
  components: {
    quillEditor,
    Picker,
  },
  props: {
    required: { type: Boolean, default: false },
    value: { type: String, default: '' },
    name: { type: String, default: '' },
    label: { type: String, default: null },
    type: { type: String, default: 'text' },
    placeholder: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    state: { type: Boolean, default: null },
    invalidFeedback: { type: String, default: null },
    validFeedback: { type: String, default: null },
    description: { type: String, default: null },
    toolbarOptions: { type: Array, default: () => [] },
    showEmojiButton: { type: Boolean, default: false },
  },
  computed: {
    editor() {
      return this.$refs.textEditors?.quill;
    },
    contentCode() {
      return hljs.highlightAuto(this.value).value;
    },
    dynamicToolbar() {
      return this.showEmojiButton
        ? ['emoji']
        : [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ['clean'],
          ];
    },
    editorOption() {
      return {
        placeholder: this.placeholder,
        modules: {
          toolbar: {
            container: this.dynamicToolbar,
            handlers: {
              emoji: this.showEmojiPicker,
            },
          },
          syntax: {
            highlight: (text) => hljs.highlightAuto(text).value,
          },
        },
      };
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateToolbarState();
      this.updateEmojiButtonText();
    });
  },
  watch: {
    disabled(newVal) {
      this.updateToolbarState(newVal);
    },
    showEmojiButton() {
      this.$nextTick(() => {
        this.updateEmojiButtonText();
      });
    },
  },
  data() {
    return {
      showEmoji: false,
      content: '',
    };
  },
  methods: {
    updateToolbarState(disabled = this.disabled) {
      const toolbar = this.$refs.textEditors?.$el?.querySelector('.ql-toolbar');
      if (toolbar) {
        if (disabled) {
          toolbar.classList.add('ql-toolbar-disabled');
        } else {
          toolbar.classList.remove('ql-toolbar-disabled');
        }
      }
    },
    updateEmojiButtonText() {
      if (!this.showEmojiButton) return;
      const emojiButton =
        this.$refs.textEditors?.$el?.querySelector('.ql-emoji');
      if (emojiButton) {
        emojiButton.innerHTML = 'Thêm icon';
      }
    },
    hideEmojiPicker() {
      setTimeout(() => {
        this.showEmoji = false;
      }, 200);
    },
    showEmojiPicker() {
      if (this.disabled || !this.showEmojiButton) return;
      this.showEmoji = true;
      const emojiButton =
        this.$refs.textEditors?.$el?.querySelector('.ql-emoji');
      if (emojiButton) {
        const rect = emojiButton.getBoundingClientRect();
        const picker = this.$el.querySelector('.emoji-picker');
        if (picker) {
          picker.style.top = `${rect.bottom + window.scrollY}px`;
          picker.style.left = `${rect.left + window.scrollX}px`;
        }
      }
    },
    addEmoji(emoji) {
      const quill = this.$refs.textEditors?.quill;
      if (quill) {
        const range = quill.getSelection(true);
        quill.insertText(range.index, emoji.native);
      }
      this.showEmoji = false;
    },
    imageHandler() {
      let range = this.editor?.getSelection();
      let value = prompt('Nhập đường dẫn ảnh ');
      if (value && this.editor) {
        this.editor.insertEmbed(range.index, 'image', value);
      }
    },
    onEditorChange: debounce(function (value) {
      this.$emit('update:value', value.html);
      this.$emit('input', value.html);
    }, 466),
    onEditorBlur() {},
    onEditorFocus() {},
    onEditorReady() {
      this.$nextTick(() => {
        this.updateEmojiButtonText();
      });
    },
  },
};
</script>

<style lang="scss">
.ql-editor.ql-blank {
  min-height: 155px;
}
.emoji-picker {
  position: absolute;
  z-index: 1000;
  background: white;
  border: 1px solid #ccc;
  width: 300px;
  height: 400px;
}
.ql-emoji {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 14px;
  background-color: #01645a !important;
  color: #fff !important;
  padding: 5px;
  height: 100%;
  width: 80px !important;
  float: none !important;
  border-radius: 5px;
}
.ql-toolbar.ql-snow {
  display: flex;
  align-items: center;
  height: 32px;
  border-color: none !important;
}
.ql-toolbar-disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
